import { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Form, InputGroup, Button, ListGroup, Badge, Accordion, Modal, Card, Nav, Tabs, Tab } from "react-bootstrap";
import { ReactComponent as TransPice } from "../../../../assets/images/icon_svg/trans_pice.svg";
import { ReactComponent as DangerError } from "../../../../assets/images/icon_svg/danger_error.svg";
import { ReactComponent as Overview } from "../../../../assets/images/icon_svg/overview.svg";
import { ReactComponent as Status } from "../../../../assets/images/icon_svg/status.svg";
import { ReactComponent as Plane } from "../../../../assets/images/icon_svg/gray_plane.svg";
import { ReactComponent as Attachment } from "../../../../assets/images/icon_svg/gray_attach.svg";
import { ReactComponent as GrayTruck } from "../../../../assets/images/icon_svg/gray_truck.svg";
import { ReactComponent as GrayClock } from "../../../../assets/images/icon_svg/gray_clock.svg";
import { ReactComponent as Open } from "../../../../assets/images/icon_svg/open_icon.svg";
import { ReactComponent as UploadHistory } from "../../../../assets/images/icon_svg/upload_history.svg";
import { ReactComponent as Delete } from "../../../../assets/images/icon_svg/upload_history.svg";
import { ReactComponent as Info } from "../../../../assets/images/icon_svg/icon_info.svg";
import { ReactComponent as AWB } from "../../../../assets/images/icon_svg/shipment.svg";
import { ReactComponent as FileDownload } from "../../../../assets/images/icon_svg/dwnld.svg";
import { ReactComponent as FileUpload } from "../../../../assets/images/icon_svg/fileupload.svg";
import { CallApi, CommonModal, getTodayDateTime } from "../../../../Components/common/CommonFunctions";
import { toast } from "react-toastify";

const STATUS_DESC = {
  ARR: "Shipment Arrived",
  AWD: "Arrival documents delivered to consignee/agent",
  DEP: "Shipment Departed",
  DIS: "Discrepancy",
  DLV: "Shipment Delivered",
  FOH: "Freight on Hand",
  MAN: "Shipment Manifested",
  NFD: "Agent has been notified",
  RCF: "Received from Flight",
  RCS: "Shipment received from shipper/agent",
};

const PILL_CLASS = {
  Planned: "planned",
  Departed: "departed",
  Arrived: "arrived",
  Manifested: "departed",
  "Shipments Received": "shipment_recived",
};

const STATUS_COLOR_MAP = {
  MAN: "man", AWD: "man", FOH: "man", NFD: "man",
  RCF: "rcf", RCS: "rcf", DEP: "rcf", ARR: "rcf", DLV: "rcf",
  DIS: "dis",
};

const val = (v) =>
  v === null || v === undefined || v === "" || v === "-" ? "-" : v;

const fmtWt = (w) =>
  typeof w === "number"
    ? w.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : val(w);

const getStatusDesc = (type, disType) => {
  if (type === "DIS")
    return disType && disType !== "-" ? `Discrepancy – ${disType}` : "Discrepancy";
  return STATUS_DESC[type] || type;
};

// ---- Attachment helpers ----
// API gives file_ext / file_type (e.g. "jpeg", "pdf", "xlsx") rather than a generic "type" field.
const getAttExt = (a) => {
  const raw = a?.file_ext || a?.file_type || a?.file_name?.split(".").pop() || "file";
  return raw.toLowerCase();
};

// jpeg -> JPG for the badge label, everything else just uppercased
const getAttBadgeLabel = (ext) => (ext === "jpeg" ? "JPG" : ext.toUpperCase());

// API returns full name, e.g. "Swissport Cargo Admin Handler" or "System"
const getAttUploader = (a) => val(a?.modified_name || a?.created_by);

// API gives last_modified ("Jun 17 2026") or upload_time ("2026-06-17 08:03:36"); prefer upload_time, formatted
const getAttDate = (a) => {
  if (a?.upload_time) return a.upload_time.substring(0, 16).replace("T", " ");
  return val(a?.last_modified);
};

const downloadAttachment = (filePath, fileName) => {
  if (!filePath) return;
  const link = document.createElement("a");
  link.href = filePath;
  link.download = fileName || "attachment";
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const TruckIcon = () => (
  <GrayTruck />
);

const PlaneIcon = () => (
  <Plane/>
);

const InfoIcon = () => (
  <Info/>
);

const StatusRow = ({ upd, truckNos = [], variant = "detailed", onFsuClick }) => {
  const tag = upd.status_type;
  const isTruck = upd.is_truck || (truckNos || []).includes(upd.flight_no);
  const fn = val(upd.flight_no);
  const fsu = upd.fsu_detail;
  const disType = fsu?.dis_type;
  const colorClass = STATUS_COLOR_MAP[tag] || "";

  if (variant === "compact") {
    return (
      <div className="status-row">
        <span className={`stag ${colorClass}`}>{tag}</span>
        <span className="sdesc">{getStatusDesc(tag, disType)}</span>
        <span className="sloc">{val(upd.station)}</span>
        <span className="stime">{val(upd.date)} {val(upd.time)}</span>
      </div>
    );
  }

 return (
    <div className={`st-row${upd.is_discrepancy ? " disc-row" : ""}`}>
      <span className={`stag ${colorClass}`}>{tag}</span>

      <div className="st-info">
        <div className={`st-name${upd.is_discrepancy ? " discrepancy-title" : ""}`}>
          {getStatusDesc(tag, disType)}

          {fn !== "-" && (
            <span className="st-flight">
              {isTruck ? <TruckIcon /> : <PlaneIcon />}
              <span className="st-truck">{fn}</span>
            </span>
          )}
        </div>

        <div className={`st-sub${upd.is_discrepancy ? " discrepancy-sub" : ""}`}>
          {val(upd.station)} · {val(upd.date)} · {val(upd.time)}
        </div>
      </div>

      <span className="fsu-tag green_btn">FSU</span>
      <span onClick={() => onFsuClick && onFsuClick(upd)} style={{ cursor: 'pointer' }}><InfoIcon /></span>
    </div>
  );
};

const AwbDetailsModal = ({ show, onHide, awbData, onDetails }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [statusFilter, setStatusFilter] = useState("All");
  const [attachmentsList, setAttachmentsList] = useState([]);
  const [localFiles, setLocalFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [showFsuModal, setShowFsuModal] = useState(false);
  const [fsuModalData, setFsuModalData] = useState(null);
  const [fsuModalLoading, setFsuModalLoading] = useState(false);

  useEffect(() => {
    if (awbData?.attachments) {
      setAttachmentsList(awbData.attachments);
    }
  }, [awbData?.attachments]);

  // Early return AFTER all hooks are declared
  if (!awbData) return null;

  const { header, overview, status, attachments = [] } = awbData;
  const det = overview?.details || {};
  const truckOrders = overview?.planned_truck_orders || [];
  const latestUpdates = overview?.latest_status_updates || [];
  const allUpdates = status?.updates || [];
  const onMultipleTrucks = status?.on_multiple_trucks || false;
  const truckNos = header?.truck_numbers || [];

  const shcDisplay =
    det.shc && det.shc !== "-"
      ? det.shc.split("-").filter((v, i, a) => a.indexOf(v) === i).join(", ")
      : "-";

  const lastUpdated = allUpdates[0]?.fsu_detail?.last_update
    ? allUpdates[0].fsu_detail.last_update.substring(0, 16).replace("T", " ")
    : "-";

  const filteredUpdates =
    statusFilter === "All"
      ? allUpdates
      : allUpdates.filter((u) => u.flight_no === statusFilter);

  const handleOpenTruck = (t) => {
    localStorage.setItem('TODetails', t.truck_monitor_mapping_id);
    const ed = t.truck_no + '/' + t.truck_date.split(' ')[0];
    onDetails('', 'TOD', ed);
  };

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleFilesAdded = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.size <= 25 * 1024 * 1024);
    if (files.length === 0) return;

    setIsUploading(true);
    const base64Files = [];

    for (const file of files) {
      try {
        const base64 = await getBase64(file);
        base64Files.push({ base64, file_name: file.name });
      } catch (err) {
        console.error("Base64 conversion failed", err);
      }
    }

    if (base64Files.length === 0) {
      setIsUploading(false);
      return;
    }

    const payload = {
      attachable_id: awbData.header?.id,
      attachable_type: "truck_order_awb",
      files: base64Files,
    };

    CallApi({
      endPoint: "attachment-save",
      method: "post",
      payload,
      onSuccess: (res) => {
        if(res?.data?.response_code == 200){
        const newAttachments = res.data?.response_data || [];
        setAttachmentsList((prev) => [...prev, ...newAttachments]);
        if (fileInputRef.current) fileInputRef.current.value = "";}
        else{
          toast.error("Failed to upload attachment")
        }
        setIsUploading(false)
      },
      onError: (err) => {console.error("Upload failed", err); toast.error("Failed to upload")}
    });
  };

  const handleDelete = (attachmentId) => {
    CallApi({
      endPoint: `attachment-delete/${attachmentId}`, // adjust to your actual delete endpoint
      method: "delete",
      onSuccess: () => {
        setAttachmentsList((prev) => prev.filter((a) => a.id !== attachmentId));
      },
      onError: (err) => console.error("Delete failed", err),
    });
  };

  const handleFsuClick = (upd) => {
    const truckingId = upd.id;
    if (!truckingId) return;
    setFsuModalLoading(true);
    CallApi({
      endPoint: "v2/service-recovery/tracking-details",
      method: "post",
      payload: { trucking_id: truckingId },
      onSuccess: (res) => {
        setFsuModalData(res.data.response_data);
        setShowFsuModal(true);
      },
      onError: (err) => {
        console.error("Failed to fetch FSU details", err);
      },
    });
  };

  return (
    <>
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="primary_modal modal_bgw recoveryCase"
    >

      <Modal.Header closeButton>
        <div className="d-flex align-items-center w-100">
          <div className="logo me-3">
            <TransPice />
          </div>

          <div>
            <div className="hd-id">
              {val(header?.awb_no)}
              {header?.has_discrepancy && (
                <Badge bg="none">
                  <DangerError /> Discrepancy
                </Badge>
              )}
            </div>

            <div className="hd-meta">
              {val(header?.board_point)} → {val(header?.off_point)} ·{" "}
              {val(header?.total_pcs)} pcs · {fmtWt(header?.total_weight)} kg
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          id="awb-detail-tabs"
          mountOnEnter
          unmountOnExit={false}
          className="mb-3"
        >

          <Tab
            eventKey="overview"
            title={<><Overview className="me-2" />Overview</>}
          >
            <div className="panel-content">
              
              <div className="ov-grid">
                <div className="ov-cell">
                  <div className="ov-label">Origin</div>
                  <div className="ov-val">{val(det.origin)}</div>
                </div>
                <div className="ov-cell">
                  <div className="ov-label">Destination</div>
                  <div className="ov-val">{val(det.destination)}</div>
                </div>
                <div className="ov-cell">
                  <div className="ov-label">Pieces</div>
                  <div className="ov-val">{val(det.pieces)} pcs</div>
                </div>
                <div className="ov-cell">
                  <div className="ov-label">Weight</div>
                  <div className="ov-val">{fmtWt(det.weight)} kg</div>
                </div>
                <div className="ov-cell">
                  <div className="ov-label">SHC</div>
                  <div className="ov-val">{shcDisplay}</div>
                </div>
              </div>

              <div className="mt-3">
                <div className="sec-hd mb-3">
                  <GrayTruck className="me-2" />Planned Truck Orders
                </div>

                <div className="truck-list">
                  {truckOrders.length === 0 && (
                    <p className="text-muted" style={{ fontSize: 13 }}>No truck orders found.</p>
                  )}

                  {truckOrders.map((t) => (
                    <div
                      key={t.truck_monitor_mapping_id}
                      className={`truck-card ${t.has_discrepancy ? "dangerTruckcrd" : "grayTruckcrd"}`}
                    >
                      <div className="tc-top">
                        <span className="tc-id">{val(t.truck_no)}</span>
                        <span className="tc_dt">{val(t.truck_date)}</span>
                        <span className={`pill ${PILL_CLASS[t.status] || "planned"}`}>
                          {val(t.status)}
                        </span>
                        <span className="spacer" />
                        <span className="tc-wt">
                          {val(t.total_pcs)} pcs / {val(t.total_weight)} kg
                        </span>
                        <Button size="sm" variant="none" onClick={() => handleOpenTruck(t)}>
                          Open <Open />
                        </Button>
                      </div>

                      <div className="tc-times">
                        <div className="time-item">
                          <GrayClock className="me-2" />STD: {val(t.std)}
                        </div>
                        <div className="time-item">
                          <GrayClock className="me-2" />STA: {val(t.sta)}
                        </div>
                      </div>

                      {t.has_discrepancy && (
                        <div className="tc-warn">
                          <DangerError className="me-2" />1 Discrepancy Detected
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Latest Status Updates */}
                <div className="sec-hd mb-3 mt-3">
                  <Status className="me-2" />Latest Status Updates
                </div>

                <div className="status-list">
                  {latestUpdates.map((upd) => (
                    <StatusRow key={upd.id} upd={upd} variant="compact" />
                  ))}
                </div>
              </div>

            </div>
          </Tab>
          <Tab
            eventKey="status"
            title={
              <>
                <Status className="me-2" />Status{" "}
                <Badge bg="none">{allUpdates.length}</Badge>
              </>
            }
          >
            <div className="panel" id="panel-status">

              {/* Multi-truck banner */}
              {/* {onMultipleTrucks && truckNos.length > 1 && ( */}
              {onMultipleTrucks  && (
                <div className="info-banner">
                  <AWB width="16" height="16" />
                  <span>
                    AWB is split across <b>{truckNos.length} truck orders</b>. DEP and MAN
                    updates show the relevant truck.
                  </span>
                </div>
              )} 

              {/* Truck filters */}
              {/* {onMultipleTrucks && truckNos.length > 1 && ( */}
              {onMultipleTrucks && (
                <div className="filter-row">
                  <span>Show:</span>
                  {["All", ...truckNos].map((f) => (
                    <Button
                      key={f}
                      variant=""
                      className={`filter-btn${statusFilter === f ? " active" : ""}`}
                      onClick={() => setStatusFilter(f)}
                    >
                      {f}
                    </Button>
                  ))}
                </div>
              )}

              <div className="status-list min-hgt-53">
                {filteredUpdates.map((upd) => (
                  <StatusRow key={upd.id} upd={upd} truckNos={truckNos} onFsuClick={handleFsuClick} />
                ))}
              </div>

            </div>
          </Tab>
         <Tab
  eventKey="attachments"
  title={
    <>
      <Attachment className="me-2" />Attachments{" "}
      <Badge bg="none">{attachmentsList.length}</Badge>
    </>
  }
>
  <div className="panel" id="panel-attachments">
    {/* Upload zone – auto‑uploads on file selection */}
    <div
      className="upload-zone"
      onDrop={(e) => {
        e.preventDefault();
        handleFilesAdded(e.dataTransfer.files);
      }}
      onDragOver={(e) => e.preventDefault()}
      onClick={() => fileInputRef.current?.click()}
      style={{ cursor: "pointer" }}
    >
      <div className="upload-icon"><FileUpload /></div>
      <div className="upload-title">Drop files here or click to upload</div>
      <div className="upload-sub">PDF, images, spreadsheets up to 25 MB</div>
      {isUploading  && <div className="mt-2 text-muted">Uploading…</div>}
      <input
        type="file"
        ref={fileInputRef}
        hidden
        accept="image/png,image/jpeg,application/pdf,.xlsx,.csv"
        onChange={(e) => handleFilesAdded(e.target.files)}
        multiple
      />
    </div>

    {/* Existing attachments list */}
    <div className="attachment-wrapper mt-4">
      <div className="sec-hd">
        <Attachment /> Attachments ({attachmentsList.length})
      </div>

                {attachmentsList.length === 0 ? (
                  <p className="text-muted" style={{ fontSize: 13, marginTop: 8 }}>
                    No attachments yet.
                  </p>
                ) : (
                  <div className="att-list min-hgt-42">
                    {attachmentsList.map((a, i) => {
                      const ext = getAttExt(a);
                      return (
                        <div key={a.id ?? i} className="att-row">
                          <div className={`att-icon ${ext}`}>
                            {getAttBadgeLabel(ext)}
                          </div>
                          <div className="att-info">
                            <div className="att-name">{val(a.file_name)}</div>
                            <div className="att-meta">
                              {val(a.file_size)} · Uploaded by {getAttUploader(a)} · {getAttDate(a)}
                            </div>
                          </div>
                          <Button
                            variant=""
                            className="btn-dl"
                            onClick={() => downloadAttachment(a.file_path, a.file_name)}
                            title="Download"
                          >
                            <FileDownload />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <p className="me-auto">Last updated: {lastUpdated}</p>
        <Button variant="none">Export</Button>
      </Modal.Footer>
    </Modal>
    <CommonModal
      show={showFsuModal}
      onHide={() => setShowFsuModal(false)}
      title='FSU Message Details'
      className='modal_bgw primary_modal modal_width_551'
      footer={<></>}
    >
      {fsuModalData && (
        <div>
          <div className='fsu_text_box'>
            <h4>{fsuModalData?.status_type} ({header?.awb_no}) - {fsuModalData?.station}</h4>
            <Row className='mb-2'>
              <Col md={4}>
                <label>Total Pieces</label>
                <p>{fsuModalData?.total_pieces_awb}</p>
              </Col>
              <Col md={4}>
                <label>Total Weight</label>
                <p>{fsuModalData?.total_weight_awb}</p>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <label>Actual Pieces</label>
                <p>{fsuModalData?.total_actual_pieces_awb}</p>
              </Col>
              <Col md={4}>
                <label>Actual Weight</label>
                <p>{fsuModalData?.total_actual_weight_awb}</p>
              </Col>
            </Row>
          </div>
          <hr/>
          {fsuModalData?.raw_fsu?.length > 0 ? (
            <div className='max-hgt-55'>
              {fsuModalData?.raw_fsu?.map((fsu, index) => (
                <div className='fsu_box' key={fsu?.id + '_' + index}>
                  <h4>{getTodayDateTime(fsu?.created_at, 'DMY')}</h4>
                  <p dangerouslySetInnerHTML={{
                    __html: (fsu?.raw_fsu_inbox?.message_data || 'No content available')
                      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
                      .replace(/\r\n/g, '<br />')
                      .trim()
                  }} />
                </div>
              ))}
            </div>
          ) : <></>}
        </div>
      )}
    </CommonModal>
    </>
  );
};

export default AwbDetailsModal;